import React, { useState } from 'react'
import { Sheet } from 'components/ui/sheet'
import { Button } from 'components/ui/button'
import { Badge } from 'components/ui/badge'
import { Trash2, X, FileCode, Calendar, HardDrive, CheckCircle2, Circle } from 'lucide-react'

interface UnusedComponent {
	componentName: string
	filePath: string
	absolutePath: string
	libraryImports: string[]
	internalImports: string[]
	hasCNVersion: boolean
	cnVersionPath?: string | null
	fileSize: number
	lastModified: string
}

interface CleanupDetailSheetProps {
	open: boolean
	onClose: () => void
	component: UnusedComponent | null
	onDelete: (filePath: string) => void
	isDeleting: boolean
}

function formatFileSize(bytes: number): string {
	if (bytes < 1024) return `${bytes} B`
	if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
	return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

function formatDate(iso: string): string {
	return new Date(iso).toLocaleDateString('en-US', {
		year: 'numeric',
		month: 'short',
		day: 'numeric',
		hour: '2-digit',
		minute: '2-digit',
	})
}

function getLibraryBadgeClass(lib: string): string {
	if (lib.startsWith('@mui') || lib === 'styled-components') return 'bg-red-100 text-red-700 border-red-200'
	if (lib === 'react' || lib === 'react-dom' || lib === 'react-redux') return 'bg-blue-100 text-blue-700 border-blue-200'
	if (lib.startsWith('lucide')) return 'bg-green-100 text-green-700 border-green-200'
	return 'bg-gray-100 text-gray-700 border-gray-200'
}

const CleanupDetailSheet: React.FC<CleanupDetailSheetProps> = ({ open, onClose, component, onDelete, isDeleting }) => {
	const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)

	const handleClose = () => {
		setShowDeleteConfirm(false)
		onClose()
	}

	if (!component) return null

	return (
		<Sheet open={open} onClose={handleClose} side="right" className="!w-[500px]">
			{/* Header */}
			<div className="flex items-center justify-between px-5 py-4 border-b border-gray-200">
				<div className="flex items-center gap-3 min-w-0">
					<div className="w-10 h-10 rounded-lg flex items-center justify-center bg-[#0080fe]/10 text-[#0080fe] flex-shrink-0">
						<FileCode className="w-5 h-5" />
					</div>
					<div className="min-w-0">
						<h2 className="text-base font-semibold text-gray-900 truncate">{component.componentName}</h2>
						<p className="text-xs text-gray-500 truncate">{component.filePath}</p>
					</div>
				</div>
				<button onClick={handleClose} className="p-1 rounded-md bg-transparent text-gray-400 hover:text-gray-600 hover:bg-gray-100">
					<X className="w-5 h-5" />
				</button>
			</div>

			{/* Body */}
			<div className="flex-1 overflow-y-auto p-5 space-y-5">
				{/* File Info */}
				<div className="space-y-3">
					<h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wider">File Info</h3>
					<div className="grid grid-cols-2 gap-3">
						<div className="flex items-center gap-2 text-sm text-gray-600">
							<HardDrive className="w-4 h-4 text-gray-400" />
							<span>{formatFileSize(component.fileSize)}</span>
						</div>
						<div className="flex items-center gap-2 text-sm text-gray-600">
							<Calendar className="w-4 h-4 text-gray-400" />
							<span>{formatDate(component.lastModified)}</span>
						</div>
					</div>
				</div>

				{/* CN Version Status */}
				<div className="space-y-3">
					<h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wider">CN Version</h3>
					<div className="flex items-center gap-2">
						{component.hasCNVersion ? (
							<>
								<CheckCircle2 className="w-4 h-4 text-green-500" />
								<span className="text-sm text-green-700">CN version exists</span>
							</>
						) : (
							<>
								<Circle className="w-4 h-4 text-gray-400" />
								<span className="text-sm text-gray-500">No CN version found</span>
							</>
						)}
					</div>
					{component.cnVersionPath && (
						<p className="text-xs text-gray-500 ml-6">{component.cnVersionPath}</p>
					)}
				</div>

				{/* Library Imports */}
				<div className="space-y-3">
					<h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wider">
						Library Imports ({component.libraryImports.length})
					</h3>
					{component.libraryImports.length > 0 ? (
						<div className="flex flex-wrap gap-1.5">
							{component.libraryImports.map((lib) => (
								<span
									key={lib}
									className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-medium ${getLibraryBadgeClass(lib)}`}
								>
									{lib}
								</span>
							))}
						</div>
					) : (
						<p className="text-sm text-gray-400">No library imports</p>
					)}
				</div>

				{/* Internal Imports */}
				<div className="space-y-3">
					<h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wider">
						Internal Imports ({component.internalImports.length})
					</h3>
					{component.internalImports.length > 0 ? (
						<div className="space-y-1">
							{component.internalImports.map((imp) => (
								<p key={imp} className="text-xs text-gray-600 font-mono bg-gray-50 rounded px-2 py-1">
									{imp}
								</p>
							))}
						</div>
					) : (
						<p className="text-sm text-gray-400">No internal imports</p>
					)}
				</div>
			</div>

			{/* Footer */}
			<div className="border-t border-gray-200 px-5 py-4 bg-gray-50">
				{showDeleteConfirm ? (
					<div className="space-y-3">
						<p className="text-sm text-red-600">
							Are you sure you want to delete <strong>{component.componentName}</strong>? This cannot be undone.
						</p>
						<div className="flex items-center gap-2">
							<Button variant="danger" onClick={() => onDelete(component.filePath)} disabled={isDeleting}>
								{isDeleting ? 'Deleting...' : 'Yes, Delete'}
							</Button>
							<Button variant="ghost" onClick={() => setShowDeleteConfirm(false)} disabled={isDeleting}>
								Cancel
							</Button>
						</div>
					</div>
				) : (
					<Button variant="ghost-red" onClick={() => setShowDeleteConfirm(true)}>
						<Trash2 className="w-4 h-4 mr-1" />
						Delete Component
					</Button>
				)}
			</div>
		</Sheet>
	)
}

export default CleanupDetailSheet
